import React, { useState } from "react";

function formatNum(n) {
  if (n == null || n === "" || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("he-IL");
}

const ROWS_PER_PAGE = 10;

/**
 * Builds the list of page-number buttons to display (max 7, with gaps collapsed to "…").
 */
function buildPageButtons(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
  const pages = new Set([0, totalPages - 1, currentPage]);
  if (currentPage > 0) pages.add(currentPage - 1);
  if (currentPage < totalPages - 1) pages.add(currentPage + 1);
  return [...pages].sort((a, b) => a - b);
}

/**
 * Amortization schedule table with page-based pagination.
 *
 * @param {Array}   rows             - Schedule rows
 * @param {string}  currency         - e.g. "ILS"
 * @param {string}  [title]          - Optional section heading
 * @param {boolean} [aggregated]     - Multi-loan view: adds lender/borrower columns, hides balance
 * @param {boolean} [noCard]         - Skip card wrapper (e.g. when parent already provides a card)
 * @param {string}  [nextPaymentDate]- ISO date of next payment (highlighted row)
 */
export default function AmortizationTable({
  rows,
  currency = "ILS",
  title,
  aggregated = false,
  noCard = false,
  nextPaymentDate,
}) {
  const [page, setPage] = useState(0);

  if (!rows || rows.length === 0) {
    return (
      <div className="card dashboard-amortization-placeholder">
        <h3>{title || "טבלת סילוקין"}</h3>
        <p className="placeholder-note">
          אין נתונים להצגה. וודא תאריך התחלה, תאריך פירעון, סוג הלוואה ותקופת תשלום.
        </p>
      </div>
    );
  }

  const totalPages = Math.ceil(rows.length / ROWS_PER_PAGE);
  const safePage = Math.min(page, totalPages - 1);
  const displayedRows = rows.slice(safePage * ROWS_PER_PAGE, (safePage + 1) * ROWS_PER_PAGE);
  const pageButtons = buildPageButtons(safePage, totalPages);

  // Determine which row is the upcoming payment (for highlight)
  const nextDateStr = nextPaymentDate
    ? new Date(nextPaymentDate).toDateString()
    : null;

  const content = (
    <>
      {title && <h3 style={{ marginBottom: "var(--space-md)" }}>{title}</h3>}
      <div style={{ overflowX: "auto" }}>
        <table className="table-minimal amortization-table">
          <thead>
            <tr>
              <th>#</th>
              <th>תאריך</th>
              {aggregated && <th>מלווה</th>}
              {aggregated && <th>לווה</th>}
              <th>תשלום</th>
              <th>קרן</th>
              <th>ריבית</th>
              {!aggregated && <th>יתרה</th>}
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((row, idx) => {
              const isNext =
                nextDateStr && row.date && new Date(row.date).toDateString() === nextDateStr;
              return (
                <tr key={idx} className={isNext ? "amort-row-next" : ""}>
                  <td>{row.periodIndex}</td>
                  <td>{formatDate(row.date)}</td>
                  {aggregated && <td>{row.lender || "—"}</td>}
                  {aggregated && <td>{row.borrowerCompany || "—"}</td>}
                  <td>
                    {formatNum(row.payment)} {currency}
                  </td>
                  <td>
                    {formatNum(row.principal)} {currency}
                  </td>
                  <td>
                    {formatNum(row.interest)} {currency}
                  </td>
                  {!aggregated && (
                    <td>
                      {formatNum(row.balance)} {currency}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="amort-pagination">
          <div className="amort-pagination-info">
            מציג {safePage * ROWS_PER_PAGE + 1}–
            {Math.min((safePage + 1) * ROWS_PER_PAGE, rows.length)} מתוך{" "}
            {rows.length} תשלומים
          </div>
          <div className="amort-pagination-controls">
            <button
              type="button"
              className="amort-page-btn"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              aria-label="עמוד קודם"
            >
              ‹
            </button>
            {pageButtons.map((pageNum, i) => {
              const prevNum = pageButtons[i - 1];
              const showEllipsis = i > 0 && pageNum - prevNum > 1;
              return (
                <React.Fragment key={pageNum}>
                  {showEllipsis && (
                    <span
                      style={{
                        color: "var(--text-secondary)",
                        fontSize: "13px",
                        padding: "0 4px",
                        lineHeight: "32px",
                      }}
                    >
                      …
                    </span>
                  )}
                  <button
                    type="button"
                    className={`amort-page-btn ${safePage === pageNum ? "current" : ""}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum + 1}
                  </button>
                </React.Fragment>
              );
            })}
            <button
              type="button"
              className="amort-page-btn"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage === totalPages - 1}
              aria-label="עמוד הבא"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </>
  );

  return noCard ? content : <div className="card">{content}</div>;
}
